const getAnalyticsConfig = require('../../../common/getAnalyticsConfig');
const getAnalyticsEventCenter = require('../../../common/getAnalyticsEventCenter');
const { HashmapStorageFactory, HASHMAP_STORAGE_TYPE } = require('../../../utilities/hashmapStorage');
const { getContactKeyForEmailAddress } = require('../../../utilities/sfmcHelpers');

const analyticsEventCenter = getAnalyticsEventCenter();
const analyticsConfig = getAnalyticsConfig();

const KEYS = {
    customerContactKey: 'contactKey',
    customerEmail: 'email',
    isOnsiteInfluencedCustomer: 'isInfluenced',
    isVisitorFromEmail: 'isEmailVisitor'
};

const storage = HashmapStorageFactory.createHashmapStorage({
    type: HASHMAP_STORAGE_TYPE.SESSION_STORAGE,
    name: 'abandonedCartCustomerSession'
});

/**
 * Get the UID of the current customer.
 * The UID is Contact Key from SFMC.
 * If the user is not tracked, return null.
 * @returns {string|null} The current customer UID
 */
function getCustomerContactKey() {
    return storage.getItem(KEYS.customerContactKey);
}

/**
 * Return true if the system cannot relate the events to a target user
 * @returns {boolean} True if it is anonymous; otherwise, false
 */
function isAnonymousCustomer() {
    return !getCustomerContactKey();
}

/**
 * Return true if the system cannot relate the events to a target user
 * @returns {boolean} True if it is anonymous; otherwise, false
 */
function isOnsiteInfluencedCustomer() {
    return storage.getItem(KEYS.isOnsiteInfluencedCustomer) === true;
}

/**
 * Returns whether the visitor originated from an email link.
 *
 * This checks the persisted `isVisitorFromEmail` flag in storage.
 *
 * @returns {boolean} `true` if the visitor came from an email; otherwise `false`.
 */
function isVisitorFromEmail() {
    return storage.getItem(KEYS.isVisitorFromEmail) === true;
}

/**
 * Init tracking
 */
function init() {
    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.abandonedCartModalContactReady,
        /**
         * Handle the event triggered after submitting the abandoned cart modal
         * form, once a valid contact has been resolved in SFMC (either newly
         * created or retrieved if it already existed).
         *
         * This event guarantees that a contactKey is available and
         * associated with the provided email.
         *
         * @param {Object} payload The payload of the event
         * @param {string} payload.contactKey The unique identifier of the related
         *   contact
         * @param {string} payload.email The email address provided by the guest
         *   user
         */
        (payload) => {
            storage.setItem(KEYS.customerEmail, payload.email);
            storage.setItem(KEYS.customerContactKey, payload.contactKey);
        }
    );

    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.userLoggedIn,
        /**
         * Handle the userLoggedIn event
         * @param {Object} payload The payload of the event
         * @param {Object} payload.authenticatedCustomer The customer info object
         * @param {string} payload.authenticatedCustomer.ID The ID of the customer
         * @param {string} payload.authenticatedCustomer.email The email of the
         *  just logged customer
         */
        async (payload) => {
            const email = payload.authenticatedCustomer.email;
            const contactKey = await getContactKeyForEmailAddress(email);
            storage.setItem(KEYS.customerEmail, email);
            storage.setItem(KEYS.customerContactKey, contactKey);
        },
        true
    );

    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.pageviewLoaded,
        /**
         * Detect any visitor that comes from the abandoned cart email
         * @param {Object} payload.referral Referral info added by middleware function
         * @param {string} payload.referral.referrerUrl Referrer url
         * @param {string} payload.referral.source Source
         */
        ({ referral } = {}) => {
            const isAbandonedCartEmailVisitor = referral.source === analyticsConfig.referral.sources.abandonedCartEmail;
            if (isAbandonedCartEmailVisitor) {
                storage.setItem(KEYS.isVisitorFromEmail, true);
            }
        }
    );

    analyticsEventCenter.subscribe(
        analyticsEventCenter.EVENTS.abandonedCartModalInteracted,
        /**
         * Track the interaction with any abandoned cart modal.
         *
         * @param {Object} payload The payload of the event
         * @param {string} payload.interaction The type of interaction
         * @param {string} payload.modalType The type of the modal (consent or save
         * my basket for guest users)
         */
        ({ interaction } = {}) => {
            const payloads = analyticsEventCenter.PAYLOADS.abandonedCartModalInteracted;
            const isPositiveInteraction = (
                interaction === payloads.interaction.continueShopping
                || interaction === payloads.interaction.submitEmailSuccess
            );
            if (isPositiveInteraction) {
                storage.setItem(KEYS.isOnsiteInfluencedCustomer, true);
            }
        }
    );
}

module.exports = {
    getCustomerContactKey,
    isOnsiteInfluencedCustomer,
    isVisitorFromEmail,
    init,
    isAnonymousCustomer
};
